<template>
  <div>
    <v-form v-model="valid" @submit.prevent="submit" ref="form">
      <!-- z-index 300 to cater for safari bug -->
      <v-autocomplete
        label="Country"
        v-model="countryOfBirth"
        :items="countries"
        item-text="name"
        item-value="code"
        :rules="rules.countryOfBirth"
        :error-messages="countryErrorMessages"
        required
        @keyup.enter="submit"
        :color="themes.candidate.light.input"
        :menu-props="{ offsetY: true }"
        style="z-index: 300"
        attach
        ref="input"
      >
        <template slot="selection" slot-scope="{ item: country }">
          <v-list-tile-avatar size="22px" style="padding: 0.5rem 1rem">
            <img
              :class="`flag-icon-background flag-icon-squared flag-icon-${country.code.toLowerCase()}`"
              style="padding: 16px"
            />
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title>{{ country.name }}</v-list-tile-title>
          </v-list-tile-content>
        </template>
        <template slot="item" slot-scope="{ item: country }">
          <v-list-tile-avatar size="22px">
            <img
              :class="`flag-icon-background flag-icon-squared flag-icon-${country.code.toLowerCase()}`"
              style="padding: 16px"
            />
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title>{{ country.name }}</v-list-tile-title>
          </v-list-tile-content>
        </template>
      </v-autocomplete>

      <v-slide-y-transition>
        <div v-if="countryOfBirth">
          <v-text-field
            v-if="countryOfBirth"
            :label="label"
            v-model="identification"
            :mask="masks[countryOfBirth]"
            :rules="rules.identification"
            required
            validate-on-blur
            :error-messages="identificationErrorMessages"
            @input="handleTextInput"
            @keyup.enter="submit"
            :color="themes.candidate.light.input"
            ref="input"
          />
        </div>
      </v-slide-y-transition>
      <div v-if="submissionError" class="error--text caption mt-2">
        {{ submissionError }}
      </div>

      <div class="text-xs-right">
        <v-btn
          v-if="hasIdentificationError"
          :color="themes.candidate.light.primaryActionButton"
          @click="openChat"
        >
          Help
          <v-icon right>mdi-help-circle</v-icon>
        </v-btn>
        <v-btn
          v-else
          :color="themes.candidate.light.primaryActionButton"
          :loading="loading"
          @click="submit"
          :disabled="!valid"
        >
          Save
        </v-btn>
      </div>
    </v-form>
  </div>
</template>

<script>
// Data
import countries from "@/data/countries";

// Helpers
import { LuhnCheck } from "@/mixins/LuhnCheck";
import { mapGetters } from "vuex";

export default {
  mixins: [LuhnCheck],

  data() {
    return {
      // Models
      countryOfBirth: "",
      identification: "",

      // Validation
      valid: false,
      rules: {
        countryOfBirth: [(v) => !!v || "must be provided"],
        identification: [
          (v) => !!v || "must be provided",
          (v) => this.isValid(v) || "must be a valid identification",
        ],
      },
      masks: {
        ZA: "###### #### ###", // ID number
        US: "###-##-####", // social security number
      },

      // Toggles
      loading: false,
      submissionError: "",
    };
  },

  computed: {
    ...mapGetters({ profile: "candidate/profile/model" }),

    countries() {
      // FIX: 'AD' causes errors to be logged
      const unsupported = ["AD"];
      return countries.filter(
        (country) => !unsupported.some((code) => code === country.code)
      );
    },

    hasChanges() {
      return (
        this.countryOfBirth !== this.profile.countryOfBirth ||
        this.identification !== this.profile.identification
      );
    },
    label() {
      if (this.countryOfBirth === "NG") return "National ID or passport number";
      if (this.countryOfBirth === "ZA") return "National ID number";
      return "National ID number";
    },
    countryErrorMessages() {
      return this.getFieldErrors(this.profile.errors, [
        "country_of_birth",
        "countryOfBirth",
      ]);
    },
    identificationErrorMessages() {
      return this.getFieldErrors(this.profile.errors, ["identification"]);
    },
    hasIdentificationError() {
      return this.identificationErrorMessages.length > 0;
    },
  },

  methods: {
    normalizeErrorMessages(errorValue) {
      if (Array.isArray(errorValue)) return errorValue.filter(Boolean);
      if (typeof errorValue === "string" && errorValue) return [errorValue];
      return [];
    },
    getFieldErrors(errors, fields) {
      if (!errors) return [];
      return fields.reduce(
        (messages, field) =>
          messages.concat(this.normalizeErrorMessages(errors[field])),
        []
      );
    },
    formatFieldName(field) {
      return field
        .replace(/_/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/^./, (char) => char.toUpperCase());
    },
    findNonIdentityError(errors) {
      if (!errors) return "";

      const identityFields = [
        "identification",
        "country_of_birth",
        "countryOfBirth",
      ];
      const keys = Object.keys(errors).filter(
        (key) => !identityFields.includes(key)
      );

      for (const key of keys) {
        const messages = this.normalizeErrorMessages(errors[key]);
        if (messages.length > 0) {
          return `${this.formatFieldName(key)} ${messages[0]}`;
        }
      }

      return "";
    },
    applyProfileUpdateError(error) {
      const responseErrors =
        (error &&
          error.response &&
          error.response.data &&
          error.response.data.errors) ||
        this.profile.errors;

      const identityErrors = this.getFieldErrors(responseErrors, [
        "identification",
        "country_of_birth",
        "countryOfBirth",
      ]);
      if (identityErrors.length > 0) {
        this.submissionError = "";
        return;
      }

      const otherError = this.findNonIdentityError(responseErrors);
      if (otherError) {
        this.submissionError = otherError;
        return;
      }

      if (this.profile.errors) {
        this.profile.errors.identification =
          "Please ensure your identification number is typed correctly or does not already exist on our system";
      }
      this.submissionError = "";
    },
    handleTextInput() {
      if (this.$refs.form.validate() && this.profile && this.profile.errors) {
        this.profile.errors.identification = "";
        this.profile.errors.country_of_birth = "";
        this.profile.errors.countryOfBirth = "";
        this.submissionError = "";
      }
    },

    isValid(id) {
      // Nigeria
      if (this.countryOfBirth === "NG") {
        return /^\d{11}$/.test(id) || /^[A-Z]\d{8}$/.test(id);
      }

      // South Africa
      if (this.countryOfBirth === "ZA") {
        return /^\d{13}$/.test(id) && this.LuhnCheck(id);
      }

      return !!(this.countryOfBirth && this.countryOfBirth.length);
    },

    openChat() {
      window.$crisp.push(["do", "chat:open"]);
      window.$crisp.push([
        "set",
        "message:text",
        [
          "Hi! It looks like my Identification number is already on Leaply. Can you help me to login?",
        ],
      ]);
    },

    submit() {
      if (this.$refs.form.validate() && this.hasChanges) {
        this.loading = true;
        this.submissionError = "";
        this.$store
          .dispatch("candidate/profile/update", {
            ...this.profile,
            identification: this.identification,
            countryOfBirth: this.countryOfBirth,
          })
          .then(
            () => {
              this.loading = false;
              this.$emit("done");
            },
            (error) => {
              this.loading = false;
              this.applyProfileUpdateError(error);
            }
          );
      }
    },
  },
};
</script>
